import { memo, useCallback, useMemo, type MemoExoticComponent } from 'react';
import { useSubscription, graphql } from 'react-relay';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	IssueUpdateSuggestionsSubscription as IssueUpdateSuggestionsSubscriptionType,
	IssueUpdateSuggestionsSubscription$data,
} from './__generated__/IssueUpdateSuggestionsSubscription.graphql.ts';
import { isClientNetworkError } from './isClientNetworkError.tsx';
import { isWebSocketError } from './isWebSocketError.tsx';

export type NudgeMetadata = {
	channelConversationId: string;
	numberOfSuggestions: number;
};

type Props = {
	appId: string;
	projectId: string;
	onEventReceived: (nudgeMetadata: NudgeMetadata) => void;
	onError?: (error: Error) => void;
};

/**
 * A React component that subscribes to issue update suggestions via GraphQL subscription.
 *
 * This component establishes a real-time subscription to receive notifications when
 * issue update suggestions are generated. It handles incoming events by extracting
 * relevant data (user ARI, source/target ARIs, suggestion counts, etc.) and fires
 * operational analytics events. When valid suggestions are received with a channel
 * conversation ID and field count > 0, it triggers the provided callback.
 *
 * @param props - The component props
 * @param props.appId - Unique identifier for the application context
 * @param props.projectId - The Jira project ID to subscribe to suggestions for
 * @param props.onEventReceived - Callback fired when valid suggestions are received
 * @param props.onError - Optional callback fired when subscription errors occur
 *
 * @returns null - This component renders nothing, it only manages the subscription
 *
 * @example
 * ```tsx
 * // Use a stable callback to handle received events to avoid re-subscription on every render
 * const handleEventReceived = useCallback(({ channelConversationId, numberOfSuggestions }) => {
 * 	...
 * }, [...]);
 *
 * // Use a stable callback to handle errors to avoid re-subscription on every render
 * const handleError = useCallback((error) => {
 * 	...
 * }, [...]);
 *
 * <IssueUpdateSuggestionsSubscription
 *   appId="my-app"
 *   projectId="10001"
 *   onEventReceived={handleEventReceived}
 *   onError={handleError}
 * />
 * ```
 */
export const IssueUpdateSuggestionsSubscription: MemoExoticComponent<
	({ appId, projectId, onEventReceived, onError }: Props) => null
> = memo(({ appId, projectId, onEventReceived, onError }: Props): null => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnEventReceived = useCallback(
		(response: IssueUpdateSuggestionsSubscription$data | null | undefined) => {
			const maybeUserAri = response?.jira_onIssueUpdateSuggestions?.userAri;
			const sourceAri = response?.jira_onIssueUpdateSuggestions?.sourceAri;
			const targetAri = response?.jira_onIssueUpdateSuggestions?.targetAri;
			const issuesCount = response?.jira_onIssueUpdateSuggestions?.suggestions?.issuesCount ?? 0;
			const fieldsCount = response?.jira_onIssueUpdateSuggestions?.suggestions?.fieldsCount ?? 0;
			const channelConversationId =
				response?.jira_onIssueUpdateSuggestions?.rovo?.channelConversationId;
			const actionMessageId = response?.jira_onIssueUpdateSuggestions?.rovo?.actionMessageId;

			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'issueUpdateSuggestionsSubscriptionEvent received',
				{
					appId,
					userAri: maybeUserAri,
					sourceAri,
					targetAri,
					channelConversationId,
					actionMessageId,
					issuesCount,
					fieldsCount,
				},
			);

			const isUserMatch =
				accountId &&
				maybeUserAri &&
				IdentityUserAri.check(maybeUserAri) &&
				IdentityUserAri.parse(maybeUserAri).userId === accountId;

			if (channelConversationId && fieldsCount > 0 && isUserMatch) {
				onEventReceived({
					channelConversationId,
					numberOfSuggestions: fieldsCount,
				});
			}
		},
		[accountId, appId, createAnalyticsEvent, onEventReceived],
	);

	const handleOnError = useCallback(
		(error: Error) => {
			log.safeErrorWithoutCustomerData(
				`IssueUpdateSuggestionsSubscription: ${appId}`,
				error.message,
				error,
			);

			if (!isClientNetworkError(error) && !isWebSocketError(error)) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'issueUpdateSuggestionsSubscriptionEvent failed',
					{
						appId,
						errorMessage: error.message,
					},
				);

				onError?.(error);
			}
		},
		[appId, createAnalyticsEvent, onError],
	);

	const variables = useMemo(
		() => ({
			cloudId,
			projectAri: JiraProjectAri.create({ siteId: cloudId, projectId }).toString(),
		}),
		[cloudId, projectId],
	);

	useSubscription<IssueUpdateSuggestionsSubscriptionType>(
		useMemo(
			() => ({
				subscription: graphql`
					subscription IssueUpdateSuggestionsSubscription($cloudId: ID!, $projectAri: ID!) {
						jira_onIssueUpdateSuggestions(cloudId: $cloudId, projectAri: $projectAri)
							@optIn(to: "JiraOnIssueUpdateSuggestions") {
							userAri
							sourceAri
							targetAri
							rovo {
								channelConversationId
								actionMessageId
							}
							suggestions {
								issuesCount
								fieldsCount
							}
						}
					}
				`,
				variables,
				onNext: handleOnEventReceived,
				onError: handleOnError,
			}),
			[handleOnError, handleOnEventReceived, variables],
		),
	);

	return null;
});
